import React, { useMemo } from 'react';

import { defaultPageDimensions, inToPx, Select } from '@aline/common';
import { animated, useSpring } from 'react-spring';
import type { FileViewerVersion } from 'src/components';
import { ConversionLoading, FileViewerBody } from 'src/components';
import { EditorSuggestionStateScope } from 'src/editor/editor-suggestion-state-registry';
import { EditorAlert } from 'src/editor/EditorAlert';
import { MobileFinishSheet } from 'src/editor/mobile/signing/MobileFinishSheet';
import { MobileSigningFieldSheet } from 'src/editor/mobile/signing/MobileSigningFieldSheet';
import { MobileWalkthroughNextButton } from 'src/editor/mobile/signing/MobileWalkthroughNextButton';
import { useMobileFitZoom, useMobilePinchZoom } from 'src/editor/mobile/use-mobile-zoom';
import { HorizontalRuler } from 'src/editor/toolbars/rulers/HorizontalRuler';
import { usePageWidthPx } from 'src/editor/vars';
import type { Apollo } from 'src/gql/generated';
import { IS_MOBILE } from 'src/helpers/environment';
import { useGetCurrentDoc, useGetEditorUserState } from 'src/hooks';
import { LayoutEngine } from 'src/layout-engine';
import {
  PAGE_OUTLINE_CLOSED_WIDTH_PX,
  useIsPageOutlineOpen,
  useOutlineWidthBasedOnBreakpoint,
} from 'src/layout-engine/components/document-outline/config';
import { useCurrentTabIdOrThrow } from 'src/layouts/tabs/tab-active-context';
import { makeStyles } from 'tss-react/mui';

import { EditorToolbar } from '.';

const useStyles = makeStyles()(theme => ({
  editorBody: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  loading: {
    backgroundColor: theme.appColors.defaultFieldBackground,
    flexGrow: 1,
    height: '100%',
    width: '100%',
  },
  main: {
    backgroundColor: theme.appColors.defaultFieldBackground,
    flexGrow: 1,
    overflowY: 'auto',
    position: 'relative',
    width: '100%',
    [theme.breakpoints.down('md')]: {
      overflowX: 'auto',
    },
  },
  scrollXContainer: {
    flexGrow: 1,
    overflowX: 'auto',
    [theme.breakpoints.down('md')]: {
      overflowX: 'hidden',
    },
  },
  wordProcessor: {
    display: 'flex',
    flexDirection: 'column',
    flexWrap: 'nowrap',
    overflow: 'hidden',
    width: '100%',
  },
}));

interface WordProcessorDoc extends Pick<Apollo.Document, 'id' | 'isSigned' | '__typename'> {
  latestVersion: Pick<Apollo.DocumentVersion, 'id' | '__typename'> | FileViewerVersion;
}

export function WordProcessor(): JSX.Element {
  const { classes } = useStyles();
  const tabId = useCurrentTabIdOrThrow();
  const { document } = useGetCurrentDoc();
  const { pageWidthPx } = usePageWidthPx();
  const isDocumentConverting = getIsDocumentConverting(document);
  const { showEditingControls } = useShowEditingControls(document);
  const outlineWidth = useOutlineWidthBasedOnBreakpoint();
  const { isPageOutlineOpen } = useIsPageOutlineOpen();
  const mobileFitZoom = useMobileFitZoom({ pageWidthPx });
  const { pinchZoom, setMainEl } = useMobilePinchZoom({ baseZoom: mobileFitZoom });

  const minWidth = useMemo(() => {
    const outlinePlaceholderWidth = isPageOutlineOpen
      ? outlineWidth * 2
      : PAGE_OUTLINE_CLOSED_WIDTH_PX;

    const renderedPageWidth = pageWidthPx || inToPx(defaultPageDimensions.width);

    return (renderedPageWidth + outlinePlaceholderWidth) * 1.1;
  }, [pageWidthPx, outlineWidth, isPageOutlineOpen]);

  const editorBodyStyle = useSpring({ minWidth: IS_MOBILE ? 0 : minWidth });

  if (isDocumentConverting) return <ConversionLoading />;
  if (!document) return <div className={classes.loading} />;

  return (
    <>
      <div className={classes.wordProcessor} data-cy={Select.document.editor.wordProcessor}>
        {showEditingControls && <EditorToolbar />}
        <div data-editor-scroll-x className={classes.scrollXContainer}>
          <animated.div className={classes.editorBody} style={editorBodyStyle}>
            {showEditingControls && <HorizontalRuler />}
            <main
              ref={setMainEl}
              data-editor-container
              className={classes.main}
              data-zoom={IS_MOBILE ? mobileFitZoom * pinchZoom : undefined}
              style={
                IS_MOBILE
                  ? {
                      flexShrink: 0,
                      height: `${100 / (mobileFitZoom * pinchZoom)}vh`,
                      transform: `scale(${mobileFitZoom * pinchZoom})`,
                      transformOrigin: 'top left',
                      width: `${100 / (mobileFitZoom * pinchZoom)}%`,
                    }
                  : undefined
              }
            >
              <EditorSuggestionStateScope tabId={tabId}>
                <DocumentEngine document={document} />
                {!IS_MOBILE && <EditorAlert document={document} />}
              </EditorSuggestionStateScope>
            </main>
            {IS_MOBILE && <MobileSigningFieldSheet />}
            {IS_MOBILE && <MobileWalkthroughNextButton />}
            {IS_MOBILE && <MobileFinishSheet />}
          </animated.div>
        </div>
      </div>
    </>
  );
}

interface DocumentEngineProps {
  document: WordProcessorDoc;
}

function DocumentEngine({ document }: DocumentEngineProps): JSX.Element {
  switch (document.latestVersion.__typename) {
    case 'DocumentVersion':
      return <LayoutEngine data-cy="editor" />;
    case 'StaticFileVersion':
      return <FileViewerBody fileVersion={document.latestVersion} isSigned={document.isSigned} />;
  }
}

type DocumentType = NonNullable<Apollo.GetDocumentBasicMetaQueryResult['data']>['document'];

function useShowEditingControls(document: DocumentType | null) {
  const { isInSigningCeremony, isReadOnly } = useGetEditorUserState();
  const isAlineDocument = document?.latestVersion.__typename === 'DocumentVersion';

  return {
    showEditingControls: isAlineDocument && !isReadOnly && !isInSigningCeremony,
  };
}

export function getIsDocumentConverting(
  document: Pick<
    Apollo.Document,
    'conversionStartedAt' | 'conversionFinishedAt' | 'conversionFailedAt'
  > | null
) {
  return Boolean(
    document?.conversionStartedAt && !document.conversionFinishedAt && !document.conversionFailedAt
  );
}
